import React, { useState } from "react";

import {
    Flex,
    Box,
    Stack,
    Text,
    SimpleGrid,
} from "@chakra-ui/react";
import {
    DollarSign,
    UserRound,
    Users,
    Flag,
    BadgeCheck,
    Wallet,
    CreditCard,
    Receipt,
} from "lucide-react";

import { useSelector } from "react-redux";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const ReviewCommissionDrawer = ({
    isOpen,
    onClose,
    commission,
    onSubmit,
}) => {
    const [formData, setFormData] =
        useState({
            payment_reference: "",
        });

    const { driverPartners } =
        useSelector(
            (state) =>
                state.driverPartners
        );

    if (!commission) return null;

    const partner =
        driverPartners.find(
            (item) =>
                item.driver_partner
                    ?.driver_partner_id ===
                commission.partner_id
        );

    const handleChange = (e) => {
        const {
            name,
            value,
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const paymentMethodMap = {
    OrangeMoney: "Orange Money",
    BankTransfer: "Bank Transfer",
    MyZaka: "MyZaka",
    Cash: "Cash",
};

const handleSubmit = () => {
    onSubmit({
        milestone_id:
            commission.milestone_id || null,
        commission_id:
            commission.commission_id || null,
        payment_method:
            paymentMethodMap[
                partner?.driver_partner?.payment_method
            ] ||
            partner?.driver_partner?.payment_method,
        payment_reference:
            formData.payment_reference,
    });

    setFormData({
        payment_reference: "",
    });
};

    return (
        <ReusableDrawer
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <Box>
                    <Text
                        fontSize="xl"
                        fontWeight="700"
                    >
                        Commission Payout
                    </Text>

                    <Text
                        color="gray.500"
                        fontSize="sm"
                    >
                        Review the
                        commission before
                        creating the
                        payout.
                    </Text>
                </Box>
            }
            bodyContent={
                <Stack spacing={6}>
                    {/* Commission Card */}
 <Box
    bgGradient="linear(to-r, blue.900, blue.700)"
    color="white"
    p={6}
    borderRadius="2xl"
>
    <Flex
        justify="space-between"
        align="center"
        mb={3}
    >
        <Text
            fontSize="xs"
            textTransform="uppercase"
            opacity={0.8}
            fontWeight="600"
            letterSpacing="1px"
        >
            Commission Amount
        </Text>

        <Flex
            align="center"
            gap={2}
            bg="whiteAlpha.200"
            px={3}
            py={1}
            borderRadius="full"
        >
            <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg="#8BC4FF"
            />

            <Text
                fontSize="xs"
                fontWeight="600"
            >
                {commission.commission_status}
            </Text>
        </Flex>
    </Flex>

    <Text
        fontSize="4xl"
        fontWeight="800"
        lineHeight="1"
    >
        P{commission.commission_amount}
    </Text>
</Box>

                    {/* Commission Information */}
                     <Box
    border="1px solid"
    borderColor="gray.200"
    borderRadius="xl"
    p={5}
>
    <Text
        fontWeight="700"
        mb={5}
           color="gray.500"
    >
        Commission Information
    </Text>

    <SimpleGrid
        columns={2}
        spacing={6}
    >
        <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <Users
            size={16}
            color="#718096"
        />

        <Text
            color="gray.500"
            fontSize="sm"
        >
            Driver Partner
        </Text>
    </Stack>

    <Text fontWeight="700">
        {commission.partner_name}
    </Text>
</Box>
      <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <UserRound
            size={16}
            color="#718096"
        />

        <Text
            color="gray.500"
            fontSize="sm"
        >
            Driver
        </Text>
    </Stack>

    <Text fontWeight="700">
        {commission.driver_name}
    </Text>
</Box>

       <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <Flag
            size={16}
            color="#718096"
        />

       <Text
        color="gray.500"
        fontSize="sm"
    >
        {commission.milestone_type
            ? "Milestone"
            : "Record Type"}
    </Text>
    </Stack>

   <StatusBadge
        label={
            commission.milestone_type ||
            commission.record_type
        }
        variant="success"
    />
</Box>

      <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <BadgeCheck
            size={16}
            color="#718096"
        />

        <Text
            color="gray.500"
            fontSize="sm"
        >
            Status
        </Text>
    </Stack>

   <StatusBadge
    label={commission.commission_status}
    variant={getStatusVariant(
        commission.commission_status
    )}
/>
</Box>
    </SimpleGrid>
</Box>

                    {/* Payment Information */}
                    <Box
    bg="gray.50"
    borderRadius="xl"
    p={5}
>
    <Text
        fontWeight="700"
           color="gray.500"
        mb={5}
    >
        Payment Details
    </Text>

    <SimpleGrid
        columns={2}
        spacing={6}
    >
       <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <Wallet
            size={16}
            color="#718096"
        />

        <Text
            color="gray.500"
            fontSize="sm"
        >
            Payment Method
        </Text>
    </Stack>

   <StatusBadge
    label={
        partner?.driver_partner
            ?.payment_method || "N/A"
    }
    variant="info"
/>
</Box>
        <Box>
    <Stack
        direction="row"
        spacing={2}
        mb={1}
        align="center"
    >
        <CreditCard
            size={16}
            color="#718096"
        />

        <Text
            color="gray.500"
            fontSize="sm"
        >
            Payment Account
        </Text>
    </Stack>

    <Text
        fontWeight="700"
        fontSize="lg"
    >
        {partner?.driver_partner?.payment_account}
    </Text>
</Box>
    </SimpleGrid>
</Box>

                    {/* Reference */}
                  <Box
    border="1px solid"
    borderColor="gray.200"
    borderRadius="xl"
    p={5}
>
    <Stack
        direction="row"
        spacing={2}
        align="center"
        mb={4}
    >
        <Receipt
            size={18}
            color="#718096"
        />

        <Text
            fontWeight="700"
               color="gray.500"
        >
            Payment Reference
        </Text>
    </Stack>

    <ReusableInput
        name="payment_reference"
        placeholder="Enter transaction reference"
        value={formData.payment_reference}
        onChange={handleChange}
    />
</Box>
                </Stack>
            }
            footerContent={
                <Stack
                    direction="row"
                    justify="flex-end"
                    spacing={4}
                >
                    <GradientButton
                        width="120px"
                        height="37px"
                        fontSize="13px"
                        onClick={
                            onClose
                        }

                    >
                        Cancel
                    </GradientButton>

                    <GradientButton
                        width="140px"
                        height="37px"
                        fontSize="13px"
                        onClick={
                            handleSubmit
                        }
                    >
                        Create
                        Payout
                    </GradientButton>
                </Stack>
            }
        />
    );
};

export default ReviewCommissionDrawer;